import React, { useEffect, useState } from "react";
import { Badge, Button, Form, InputGroup, Placeholder, Table } from "react-bootstrap";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash, faBoxesStacked, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import { toast } from "react-toastify";
import Pagination from "../Common/Pagination";

const ProductList = ({
  workspaceId,
  setSelectedProduct,
  setProductPopupShow,
  setStockProduct,
  setStockPopupShow,
  reload,
}) => {
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const limit = 20;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const query = { workspace: workspaceId, page, limit };
        if (searchTerm) query.search = searchTerm;

        const response = await Utils._apiRequest({
          path: Enums.endpoints.supplierStock.PRODUCTS,
          type: "GET",
          query,
        });
        setLoading(false);
        setProductList(response.data || []);
        setCount(response.count || 0);
      } catch (error) {
        setLoading(false);
        console.log("Failed to fetch products:", error.message);
      }
    };

    setLoading(true);
    fetchProducts();
  }, [workspaceId, searchTerm, reload, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchTerm(search);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setProductPopupShow(true);
  };

  const handleStockEdit = (product) => {
    setStockProduct(product);
    setStockPopupShow(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm("Bạn có chắc muốn xoá sản phẩm này?")) {
      try {
        const res = await Utils._apiRequest({
          path: `${Enums.endpoints.supplierStock.PRODUCTS}/${productId}`,
          type: "DELETE",
        });
        if (res.errors) throw new Error(res.errors);
        toast.success("Xoá sản phẩm thành công");
        setProductList(productList.filter((p) => p._id !== productId));
      } catch (error) {
        toast.error(error.message || "Lỗi khi xoá sản phẩm");
      }
    }
  };

  const renderStockBadges = (supplierStock) => {
    if (!supplierStock || supplierStock.length === 0) {
      return <span className="text-muted">--</span>;
    }
    return (
      <div className="supplier-stock-table__stock-badges">
        {supplierStock.map((s, idx) => {
          const supplierName = s.supplier?.name || "Unknown";
          const isInstock = s.status === "instock";
          return (
            <span
              key={idx}
              className={`supplier-stock-table__stock-pill ${isInstock ? "supplier-stock-table__stock-pill--instock" : "supplier-stock-table__stock-pill--outofstock"}`}
              title={s.note ? `${supplierName}: ${s.note}` : supplierName}
            >
              {supplierName}: {isInstock ? "Còn hàng" : "Hết hàng"}
              {s.note && <small className="ms-1">({s.note})</small>}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Form onSubmit={handleSearch} className="mb-3">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Tìm theo SKU hoặc tên sản phẩm..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="outline-secondary" type="submit">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </Button>
        </InputGroup>
      </Form>

      <OverlayScrollbarsComponent
        style={{ maxWidth: "100%" }}
        options={{ scrollbars: { autoHide: "scroll" } }}
      >
        <Table striped hover className="table-text-inline supplier-stock-table">
          <thead>
            <tr>
              <th style={{ width: 20 }}>#</th>
              <th style={{ width: 60 }}>Ảnh</th>
              <th>SKU</th>
              <th>Tên sản phẩm</th>
              <th>Tình trạng stock</th>
              <th style={{ width: 170 }} className="text-end">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(5).keys()].map((i) => (
                  <tr key={i} className="placeholder-group">
                    <td>{i + 1}</td>
                    <td><Placeholder animation="glow"><Placeholder xs={12} style={{ height: 40 }} /></Placeholder></td>
                    <td><Placeholder animation="glow"><Placeholder xs={8} /></Placeholder></td>
                    <td><Placeholder animation="glow"><Placeholder xs={10} /></Placeholder></td>
                    <td><Placeholder animation="glow"><Placeholder xs={6} /></Placeholder></td>
                    <td className="text-end"><Button size="sm" disabled variant="secondary"></Button></td>
                  </tr>
                ))}
              </>
            ) : (
              <>
                {productList && productList.length > 0 ? (
                  productList.map((product, i) => (
                    <tr key={product._id || i}>
                      <td>{(page - 1) * limit + i + 1}</td>
                      <td>
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.productName}
                            className="supplier-stock-table__product-img"
                          />
                        ) : (
                          <div className="supplier-stock-table__product-img-placeholder">--</div>
                        )}
                      </td>
                      <td><Badge bg="dark">{product.sku}</Badge></td>
                      <td><strong>{product.productName}</strong></td>
                      <td>{renderStockBadges(product.supplierStock)}</td>
                      <td className="text-inline text-end">
                        <div className="supplier-stock-table__actions">
                          <Button className="ms-1" size="sm" variant="outline-info" title="Quản lý stock" onClick={() => handleStockEdit(product)}>
                            <FontAwesomeIcon icon={faBoxesStacked} />
                          </Button>
                          <Button className="ms-1" size="sm" variant="outline-secondary" title="Sửa" onClick={() => handleEdit(product)}>
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </Button>
                          <Button className="ms-1" size="sm" variant="outline-danger" title="Xoá" onClick={() => handleDelete(product._id)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="supplier-stock-table__empty">
                        <i>{searchTerm ? "Không tìm thấy sản phẩm phù hợp." : "Chưa có sản phẩm nào trong workspace này."}</i>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </Table>
      </OverlayScrollbarsComponent>

      <Pagination page={page} setPage={setPage} count={count} limit={limit} setLoading={setLoading} />
    </>
  );
};

export default ProductList;
